import { createSlice } from '@reduxjs/toolkit';
import { dispatch, store } from '../store';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import {
    hasAdsReportingFeatureAccess,
    isCurrentAdsReportingPractice,
    resolveAdsPracticeId,
} from './adsReportingScope';

const createRequestDomain = () => ({
    requestKey: null,
    requestId: null,
    status: 'idle',
    error: null,
    data: null,
});

export const TIKTOK_RECOVERY_CODES = new Set([
    'TIKTOK_ADVERTISER_RESELECT_REQUIRED',
    'TIKTOK_RECONNECT_REQUIRED',
    'TIKTOK_INSIGHTS_UNAVAILABLE',
]);

const createInitialState = (legacyRequestGeneration = 0) => ({
    legacyRequestGeneration,
    legacyAccountsRequestId: null,
    isLoading: false,
    error: null,
    connectionRecovery: null,
    accounts: [],
    report: createRequestDomain(),
    periodComparison: createRequestDomain(),
    crossPractice: createRequestDomain(),
});

const initialState = createInitialState();

const slice = createSlice({
    name: 'tiktokAds',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        setConnectionRecovery(state, action) {
            state.connectionRecovery = action.payload;
        },
        clearConnectionRecovery(state) {
            state.connectionRecovery = null;
        },
        setAccounts(state, action) {
            state.accounts = action.payload;
        },
        legacyAccountsRequestStarted(state, action) {
            state.legacyAccountsRequestId = action.payload;
            state.isLoading = true;
        },
        requestStarted(state, action) {
            const { domain, requestKey, requestId } = action.payload;
            const requestState = state[domain];
            if (!requestState) return;
            requestState.requestKey = requestKey;
            requestState.requestId = requestId;
            requestState.status = 'loading';
            requestState.error = null;
            if (domain === 'report' || domain === 'periodComparison') {
                state.connectionRecovery = null;
            }
        },
        requestSucceeded(state, action) {
            const { domain, requestKey, requestId, data } = action.payload;
            const requestState = state[domain];
            if (
                !requestState
                || requestState.requestKey !== requestKey
                || requestState.requestId !== requestId
            ) return;
            requestState.status = 'succeeded';
            requestState.error = null;
            requestState.data = data;
            if (domain === 'report' || domain === 'periodComparison') {
                state.connectionRecovery = null;
            }
        },
        requestFailed(state, action) {
            const {
                domain,
                requestKey,
                requestId,
                error,
                connectionRecovery: requestRecovery,
            } = action.payload;
            const requestState = state[domain];
            if (
                !requestState
                || requestState.requestKey !== requestKey
                || requestState.requestId !== requestId
            ) return;
            requestState.status = 'failed';
            requestState.error = error;
            if ((domain === 'report' || domain === 'periodComparison') && requestRecovery) {
                state.connectionRecovery = requestRecovery;
            }
        },
        resetLegacy(state) {
            state.legacyRequestGeneration = (Number(state.legacyRequestGeneration) || 0) + 1;
            state.legacyAccountsRequestId = null;
            state.isLoading = false;
            state.error = null;
            state.connectionRecovery = null;
            state.accounts = [];
        },
        invalidateLegacyRequests(state) {
            state.legacyRequestGeneration = (Number(state.legacyRequestGeneration) || 0) + 1;
            state.legacyAccountsRequestId = null;
            state.isLoading = false;
            state.error = null;
            state.connectionRecovery = null;
        },
        reset(state) {
            const nextGeneration = (Number(state.legacyRequestGeneration) || 0) + 1;
            return createInitialState(nextGeneration);
        },
    },
});

export const {
    clearConnectionRecovery: clearTikTokConnectionRecovery,
    clearError: clearTikTokError,
    reset: resetTikTokAds,
    resetLegacy: resetLegacyTikTokAds,
    invalidateLegacyRequests: invalidateLegacyTikTokAdsRequests,
} = slice.actions;

export default slice.reducer;

// --------------- Helpers ---------------

function getRequestScope(explicitPracticeId) {
    return resolveAdsPracticeId(explicitPracticeId, store.getState());
}

function hasScopeAccess(featureName, scope) {
    return scope.reportScoped
        ? hasAdsReportingFeatureAccess(featureName, scope.practiceId, store.getState())
        : hasFeatureAccess(featureName, scope.practiceId);
}

function isScopeCurrent(scope) {
    return !scope.reportScoped || isCurrentAdsReportingPractice(scope.practiceId, store.getState());
}

function getLegacyRequestGeneration() {
    return Number(store.getState()?.tiktokAds?.legacyRequestGeneration) || 0;
}

function isLegacyLifecycleCurrent(scope, generation) {
    return isScopeCurrent(scope) && getLegacyRequestGeneration() === generation;
}

function isLegacyRequestCurrent(scope, generation, requestIdKey, requestId) {
    const tiktokAdsState = store.getState()?.tiktokAds;
    return isLegacyLifecycleCurrent(scope, generation)
        && tiktokAdsState?.[requestIdKey] === requestId;
}

function dispatchForLegacyRequest(scope, generation, requestIdKey, requestId, action) {
    if (isLegacyRequestCurrent(scope, generation, requestIdKey, requestId)) dispatch(action);
}

const SCOPE_KEYS = ['practice_id', 'account_id', 'advertiser_id'];
const QUERY_KEY_ORDER = [
    'level',
    'parent_id',
    'date_from',
    'date_to',
    'search',
    'ordering',
    'page',
    'page_size',
    'period_a_start',
    'period_a_end',
    'period_b_start',
    'period_b_end',
    'campaign_page',
    'campaign_page_size',
    'campaign_search',
    'campaign_ordering',
];
const QUERY_ALIASES = new Set([
    ...SCOPE_KEYS,
    'practiceId',
    'accountId',
    'adAccountId',
    'practice_ids',
]);

function hasRequestValue(value) {
    return value !== null && value !== undefined && value !== '';
}

function readValue(source, camelKey, apiKey) {
    if (!source || typeof source !== 'object') return undefined;
    return source[camelKey] ?? source[apiKey];
}

function readScopedValue(scope, query, camelKey, apiKey) {
    if (scope && typeof scope === 'object') {
        if (Object.prototype.hasOwnProperty.call(scope, camelKey)) return scope[camelKey];
        if (Object.prototype.hasOwnProperty.call(scope, apiKey)) return scope[apiKey];
    }
    return readValue(query, camelKey, apiKey);
}

function normalizeScope(scope, query = {}) {
    let source = {};
    if (scope && typeof scope === 'object') {
        source = scope;
    } else if (scope !== null && scope !== undefined) {
        source = { practiceId: scope };
    }
    return {
        practice_id: readScopedValue(source, query, 'practiceId', 'practice_id'),
        account_id: readScopedValue(source, query, 'accountId', 'account_id'),
        advertiser_id: readScopedValue(source, query, 'adAccountId', 'advertiser_id'),
    };
}

function compareParamValues(left, right) {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber - rightNumber;
    return String(left).localeCompare(String(right));
}

function normalizeRepeatedValues(value) {
    const values = Array.isArray(value) ? value : [value];
    return [...new Set(values.filter(hasRequestValue).map(String))].sort(compareParamValues);
}

function appendRequestValue(params, key, value) {
    if (!hasRequestValue(value)) return;
    if (Array.isArray(value)) {
        value.filter(hasRequestValue).forEach((item) => params.append(key, String(item)));
        return;
    }
    params.append(key, String(value));
}

function orderedQueryKeys(query) {
    const keys = Object.keys(query || {}).filter((key) => !QUERY_ALIASES.has(key));
    const known = QUERY_KEY_ORDER.filter((key) => keys.includes(key));
    const unknown = keys.filter((key) => !QUERY_KEY_ORDER.includes(key)).sort();
    return [...known, ...unknown];
}

function buildRequestParams(query = {}, scope = null, { crossPractice = false } = {}) {
    const params = new URLSearchParams();
    if (crossPractice) {
        const selectedPracticeIds = query.practice_ids ?? query.practice_id;
        normalizeRepeatedValues(selectedPracticeIds).forEach((practiceId) => {
            params.append('practice_id', practiceId);
        });
        appendRequestValue(params, 'account_id', readValue(query, 'accountId', 'account_id'));
        appendRequestValue(params, 'advertiser_id', readValue(query, 'adAccountId', 'advertiser_id'));
    } else {
        const normalizedScope = normalizeScope(scope, query);
        SCOPE_KEYS.forEach((key) => appendRequestValue(params, key, normalizedScope[key]));
    }
    orderedQueryKeys(query).forEach((key) => appendRequestValue(params, key, query[key]));
    return params;
}

function buildRequestUrl(path, params) {
    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
}

// A response body that failed to parse as JSON (a proxy/server error page —
// 404/502/etc — served as HTML instead of the API's JSON contract) comes
// through as a plain string here. Never surface that raw markup as a "human"
// error message; only strings that don't look like a document body are
// eligible.
const HTML_DOCUMENT_PATTERN = /^\s*<(!doctype\s+html|html|head|body)\b/i;

function firstErrorMessage(value) {
    if (typeof value === 'string' && value.trim() && !HTML_DOCUMENT_PATTERN.test(value)) return value;
    if (Array.isArray(value)) {
        return value.map(firstErrorMessage).find(Boolean) || null;
    }
    if (value && typeof value === 'object') {
        const priorityMessage = ['detail', 'message', 'error']
            .map((key) => firstErrorMessage(value[key]))
            .find(Boolean);
        return priorityMessage || Object.values(value).map(firstErrorMessage).find(Boolean) || null;
    }
    return null;
}

function normalizeRequestError(response, fallback) {
    return firstErrorMessage(response?.data)
        || firstErrorMessage(response?.error)
        || firstErrorMessage(response)
        || fallback;
}

function isSuccessfulResponse(response) {
    return response?.ok === true
        || (Number(response?.status) >= 200 && Number(response?.status) < 300);
}

let reportRequestSequence = 0;
let legacyRequestSequence = 0;
const legacyRequestInstancePrefix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

function nextLegacyRequestId() {
    legacyRequestSequence += 1;
    return `${legacyRequestInstancePrefix}-${legacyRequestSequence}`;
}

function createReportRequest(domain, path, query, scope, options = {}) {
    return async () => {
        const params = buildRequestParams(query, scope, options);
        const url = buildRequestUrl(path, params);
        const requestKey = `GET:${url}`;
        reportRequestSequence += 1;
        const requestId = reportRequestSequence;
        dispatch(slice.actions.requestStarted({ domain, requestKey, requestId }));
        try {
            const ownsConnectionRecovery = domain === 'report' || domain === 'periodComparison';
            const response = ownsConnectionRecovery
                ? await callApi(
                    url,
                    'GET',
                    null,
                    false,
                    { suppressServerErrorDispatch: true }
                )
                : await callApi(url);
            if (isSuccessfulResponse(response)) {
                dispatch(slice.actions.requestSucceeded({
                    domain,
                    requestKey,
                    requestId,
                    data: response.data,
                }));
                return response.data;
            }
            const error = normalizeRequestError(response, options.fallbackError);
            const recoveryCode = response?.data?.error_code;
            const connectionRecovery = ownsConnectionRecovery && TIKTOK_RECOVERY_CODES.has(recoveryCode)
                ? {
                    code: recoveryCode,
                    message: response.data?.message,
                    advertiser_id: response.data?.advertiser_id,
                }
                : null;
            dispatch(slice.actions.requestFailed({
                domain,
                requestKey,
                requestId,
                error,
                connectionRecovery,
            }));
        } catch (error) {
            dispatch(slice.actions.requestFailed({
                domain,
                requestKey,
                requestId,
                error: normalizeRequestError(error, options.fallbackError),
            }));
        }
        return null;
    };
}

// --------------- Thunks ---------------

/**
 * Fetch connected TikTok accounts for this practice.
 */
export function fetchTikTokAccounts(explicitPracticeId) {
    return async () => {
        const scope = getRequestScope(explicitPracticeId);
        const generation = getLegacyRequestGeneration();
        const { practiceId } = scope;
        if (!practiceId) return;
        if (!hasScopeAccess('tiktok-integration', scope)) return;
        if (!isLegacyLifecycleCurrent(scope, generation)) return;
        const requestId = nextLegacyRequestId();
        dispatch(slice.actions.legacyAccountsRequestStarted(requestId));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/`);
            if (response.status === 200) {
                dispatchForLegacyRequest(
                    scope,
                    generation,
                    'legacyAccountsRequestId',
                    requestId,
                    slice.actions.setAccounts(response.data?.results || [])
                );
            }
        } catch (error) {
            dispatchForLegacyRequest(
                scope,
                generation,
                'legacyAccountsRequestId',
                requestId,
                updateError({ success: false, message: error.message || 'Failed to fetch TikTok accounts' })
            );
        } finally {
            dispatchForLegacyRequest(
                scope,
                generation,
                'legacyAccountsRequestId',
                requestId,
                slice.actions.setLoading(false)
            );
        }
    };
}

/**
 * Fetch a cached single-practice TikTok reporting view. The supplied scope owns
 * the practice/account selection; it never changes the CRM's global practice.
 */
export function fetchTikTokReport(query = {}, scope = null) {
    return createReportRequest(
        'report',
        '/api/v1/tiktok-ads/report/',
        query,
        scope,
        { fallbackError: 'TikTok reporting could not be loaded. Try again.' }
    );
}

/** Fetch a cached Campaign comparison for two explicit reporting periods. */
export function fetchTikTokPeriodComparison(query = {}, scope = null) {
    return createReportRequest(
        'periodComparison',
        '/api/v1/tiktok-ads/period-compare/',
        query,
        scope,
        { fallbackError: 'Failed to compare TikTok reporting periods' }
    );
}

/**
 * Fetch all accessible practices by default. `practice_ids`, when present,
 * becomes repeated `practice_id` parameters for an explicitly narrowed view.
 */
export function fetchTikTokCrossPractice(query = {}) {
    return createReportRequest(
        'crossPractice',
        '/api/v1/tiktok-ads/cross-practice-compare/',
        query,
        null,
        {
            crossPractice: true,
            fallbackError: 'Failed to fetch cross-practice TikTok report',
        }
    );
}

/**
 * Queue a bounded backend refresh. The endpoint consumes JSON request data and
 * returns safe HTTP 202 metadata (`accepted` or `already_queued`) without
 * exposing provider credentials or Celery task IDs.
 */
export function refreshTikTokMetrics(query = {}, scope = null) {
    return async () => {
        const params = buildRequestParams(query, scope);
        const payload = Object.fromEntries(params.entries());
        try {
            const response = await callApi('/api/v1/tiktok-ads/refresh/', 'POST', payload);
            if (isSuccessfulResponse(response) && response.status === 202) return response.data;
            return {
                status: 'failed',
                count: 0,
                jobs: [],
                error: normalizeRequestError(response, 'Failed to refresh TikTok metrics'),
            };
        } catch (error) {
            return {
                status: 'failed',
                count: 0,
                jobs: [],
                error: normalizeRequestError(error, 'Failed to refresh TikTok metrics'),
            };
        }
    };
}
